import { observer } from 'mobx-react-lite';
import React, { Fragment, useCallback } from 'react';
import { CellItem } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { GridCard } from '../GridCard/GridCard';
import { PreviewGridCard } from '../GridCard/PreviewGridCard';
import { useTimetableStore } from '../TimetableProvider';
import style from './GridCards.module.scss';

export const GridCards = observer(({ cell }: { cell: CellItem }) => {
  const store = useTimetableStore();

  // Preview only takes over once the solver has produced placements (previewActive).
  // While previewing, hide the persisted cards entirely — only the solver's ghost cards
  // are shown, otherwise the old and proposed placements overlap and read as noise.
  const previewActive = store.solverStore.previewActive;
  const cardsArray = previewActive ? [] : store.getPlacedCardsForCell(cell);
  const previewCards = previewActive ? store.getPreviewCardsForCell(cell) : [];
  const handleClick = useCallback(() => {
    store.placeCard(cell);
  }, [cell, store]);

  if (cardsArray.length === 0 && previewCards.length === 0) {
    return null;
  }

  const renderPreviewCard = (card: (typeof previewCards)[number]) => {
    const borders = ((card.duration ?? 1) - 1) * 2;
    const width = `calc(${(card.duration ?? 1) * 100}% + ${borders}px)`;
    const gridCards = store.gridCards.get(card.uuid) ?? [];

    if (store.context === 'classes') {
      return gridCards
        .filter((cc) => cc.classUuid === cell.row.value)
        .map((cc) => (
          <PreviewGridCard
            key={`preview-${cc.timetableCardUuid}-${cc.uuid}`}
            width={width}
            height={cc.cssHeight ?? '100%'}
            left={'0%'}
            top={cc.cssTop ?? '0%'}
            background={cc.cssGradientString ?? ''}
            textColor={cc.textColor ?? ''}
            card={card}
          />
        ));
    }

    const cc = gridCards.find((entry) => entry.timetableCardUuid === card.uuid);
    if (!cc) {
      return <Fragment key={`preview-${card.uuid}`}></Fragment>;
    }
    return (
      <PreviewGridCard
        key={`preview-${cc.timetableCardUuid}-${cc.uuid}`}
        width={width}
        height={store.context === 'rooms' ? '100%' : (cc.cssHeight ?? '100%')}
        left={'0%'}
        top={store.context === 'rooms' ? '0%' : (cc.cssTop ?? '0%')}
        background={cc.cssGradientString ?? ''}
        textColor={cc.textColor ?? ''}
        card={card}
      />
    );
  };

  // Koexistierbare Karten (elective/attendance) dürfen sich eine Cell teilen; ihre Höhe wird
  // gestapelt. Teilung und Spur kommen je Karte aus getCoexistLayout – eine Mehrfachstunde
  // richtet sich nach ihrer engsten Periode, damit sie bündig durchläuft.
  const { coexisting, coexistCards, coexistLayout } = store.getCoexistLayout(
    cardsArray,
    cell.day.value,
    cell.row.value,
  );

  return (
    <div className={style['grid-card-wrapper']} onClick={handleClick}>
      {coexistCards.map((card) => {
        const borders = ((card.duration ?? 1) - 1) * 2;
        const gridCards = store.gridCards.get(card.uuid) ?? [];

        if (card.startTimeGridEntry?.uuid !== cell.hour.value) {
          return <Fragment key={card.uuid}></Fragment>;
        }

        if (coexisting) {
          const cc = gridCards.find((entry) => entry.classUuid === cell.row.value) ?? gridCards[0];
          const { track, divisions } = coexistLayout.get(card.uuid) ?? { track: 0, divisions: 1 };
          return (
            <Fragment key={card.uuid}>
              <GridCard
                width={`calc(${(card.duration ?? 1) * 100}% + ${borders}px)`}
                height={`${100 / divisions}%`}
                left={'0%'}
                top={`${(track * 100) / divisions}%`}
                background={cc?.cssGradientString ?? ''}
                subjectColor={card.subject?.timetableConfig?.color ?? ''}
                textColor={cc?.textColor ?? ''}
                card={card}
                cell={cell}
              />
            </Fragment>
          );
        }

        if (store.context === 'classes') {
          return gridCards
            ?.filter((cc) => {
              return cc.classUuid === cell.row.value;
            })
            .map((cc) => {
              return (
                <Fragment key={`${cc.timetableCardUuid}-${cc.uuid}`}>
                  <GridCard
                    width={`calc(${(card.duration ?? 1) * 100}% + ${borders}px)`}
                    height={cc.cssHeight ?? '100%'}
                    left={'0%'}
                    top={cc.cssTop ?? '0%'}
                    background={cc.cssGradientString ?? ''}
                    subjectColor={card.subject?.timetableConfig?.color ?? ''}
                    textColor={cc.textColor ?? ''}
                    card={card}
                    cell={cell}
                  />
                </Fragment>
              );
            });
        }
        if (store.context === 'rooms') {
          const cc = gridCards?.find((cc) => cc.timetableCardUuid === card.uuid);
          if (cc) {
            return (
              <Fragment key={`${cc.timetableCardUuid}-${cc.uuid}`}>
                <GridCard
                  width={`calc(${(card.duration ?? 1) * 100}% + ${borders}px)`}
                  height={'100%'}
                  left={'0%'}
                  top={'0%'}
                  background={cc.cssGradientString ?? ''}
                  subjectColor={card.subject?.timetableConfig?.color ?? ''}
                  textColor={cc.textColor ?? ''}
                  card={card}
                  cell={cell}
                />
              </Fragment>
            );
          }
          return <Fragment key={card.uuid}></Fragment>;
        }
        if (store.context === 'teachers') {
          const cc = gridCards?.find((cc) => cc.timetableCardUuid === card.uuid);
          if (cc) {
            return (
              <Fragment key={`${cc.timetableCardUuid}-${cc.uuid}`}>
                <GridCard
                  width={`calc(${(card.duration ?? 1) * 100}% + ${borders}px)`}
                  height={cc.cssHeight ?? '100%'}
                  left={'0%'}
                  top={cc.cssTop ?? '0%'}
                  background={cc.cssGradientString ?? ''}
                  subjectColor={card.subject?.timetableConfig?.color ?? ''}
                  textColor={cc.textColor ?? ''}
                  card={card}
                  cell={cell}
                />
              </Fragment>
            );
          }
          return <Fragment key={card.uuid}></Fragment>;
        }
        return <Fragment key={card.uuid}></Fragment>;
      })}
      {previewCards.map((card) => (
        <Fragment key={`preview-wrap-${card.uuid}`}>{renderPreviewCard(card)}</Fragment>
      ))}
    </div>
  );
});
GridCards.displayName = 'GridClassCell';
